import type { ISendReceiptMailCmd } from "@/data/commands/order/SendReceiptMailCmd";
import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { Language, ReceiptType } from "@/data/model/Enums";
import { Validation } from "@/data/util/Validation";

import { BaseModal } from "@/shared/elements/BaseModal";
import { languageSelectList } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";

export class SendOrderReceiptWindow extends BaseModal {
    private orderDao = Factory.getInstance<OrderDao>(OrderDao);
    private sourceId: string;
    private readonly receiptType: ReceiptType;
    private readonly locationLanguage?: Language;
    private $form: JQuery;
    private $submitBtn: JQuery;

    private languageSelect: Select<Language>;
    private $receiverField: JQuery;

    constructor(orderId: string, receiptType: ReceiptType, locationLanguage: Language | undefined) {
        super(i18n("modal.sendOrderReceipt.title"), "modal-lg", false);
        this.sourceId = orderId;
        this.receiptType = receiptType;
        this.locationLanguage = locationLanguage;

        this.$element.addClass("sendorderreceipt");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.sendOrderReceipt.settings") +
                "</h3></div></div>"
        );

        const languageGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.fields.language") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const select = $('<select class="form-control" />').appendTo(languageGroup);
        languageSelectList.forEach((item) => {
            select.append(`<option value="${item.id}">${i18n("enums.Language." + item.name)}</option>`);
        });
        this.languageSelect = Select.createFromSelect(select);
        this.languageSelect.setValue(
            this.locationLanguage ?? TastierGlobal.getLocationSwitch()?.getCurrentLocationLanguage() ?? Language.English
        );

        const receiverGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.sendOrderReceipt.receiver") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6"></div></div>'
        ).appendTo(createSection);
        this.$receiverField = $(
            '<input type="text" name="receiver" class="form-control" placeholder="max.muster@email.com" />'
        ).appendTo(receiverGroup.find("> .col-xl-6"));

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.send") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSend();

            return false;
        });
    }

    protected onShow() {}

    protected onHide() {}

    private onSend() {
        const data: ISendReceiptMailCmd = {
            language: parseInt(this.languageSelect.getValue()!),
            receiver: this.$receiverField.val().length === 0 ? undefined : this.$receiverField.val(),
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (!Validation.isEmail(data.receiver)) {
            this.$receiverField.addClass("is-invalid");
            hasError = true;
        }

        if (hasError) return;

        switch (this.receiptType) {
            case ReceiptType.Order:
                this.sendOrderReceipt(data);
                break;
            case ReceiptType.Payment:
                this.sendPaymentReceipt(data);
                break;
        }
    }

    private async sendOrderReceipt(data: ISendReceiptMailCmd) {
        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        try {
            await this.orderDao.sendOrderReceiptMail(this.sourceId, data);

            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            UiNotification.showSuccess(i18n("modal.sendOrderReceipt.sentTo", { receiver: data.receiver }));
            this.close();
        } catch (ex) {
            logError(ex);
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            UiNotification.showException(i18n("modal.sendOrderReceipt.failedSending"));
        }
    }

    private async sendPaymentReceipt(data: ISendReceiptMailCmd) {
        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        try {
            await this.orderDao.sendPaymentReceiptMail(this.sourceId, data);

            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            UiNotification.showSuccess(i18n("modal.sendOrderReceipt.sentTo", { receiver: data.receiver }));
            this.close();
        } catch (ex) {
            logError(ex);
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            UiNotification.showException(i18n("modal.sendOrderReceipt.failedSending"));
        }
    }
}
